import axios, { AxiosInstance, AxiosError, AxiosResponse } from 'axios';

import { notifyPasswordChangeRequired, notifySessionExpired } from './session-expired';

const HOST_GROWME = process.env.REACT_APP_HOST_GROWME as string;

export const HOST_PUBLIC = process.env.REACT_APP_HOST_PUBLIC as string;

// Ruta del mismo dominio del front que Vercel reenvía al back (rewrites en
// vercel.json). Sirve cuando la red de la persona (firewall o proxy de empresa)
// deja abrir la página pero bloquea el dominio del back: el pedido sale hacia el
// mismo dominio que ya cargó. Sin entrada (p. ej. localhost) no hay alternativa.
const BACKEND_PROXY_PATHS: Record<string, string> = {
  'https://superadmin-back.growmeapp.ai': '/_backend/prod',
  'https://superadmin-back-dev.growmeapp.ai': '/_backend/dev',
};
const BACKEND_PROXY_PATH = BACKEND_PROXY_PATHS[(HOST_GROWME || '').replace(/\/+$/, '')];

let useBackendProxy = false;

export const canUseBackendProxy = (): boolean => !!BACKEND_PROXY_PATH;

// Desde aquí en adelante (hasta recargar la página) todos los pedidos van por el proxy.
export const enableBackendProxy = (): void => {
  if (BACKEND_PROXY_PATH) useBackendProxy = true;
};

interface TokenData {
  accessToken: string;
}

const getAccessToken = async (): Promise<TokenData> => {
  return new Promise((resolve, reject) => {
    const token = localStorage.getItem('token');
    if (!token) {
      return reject({ accessToken: "" });
    }

    resolve({ accessToken: token });
  });
}

// El back solo responde 401 cuando el token falta, es inválido o expiró
// (authMiddleware / AuthValidateSessionController), y no hay refresh token:
// reintentar con el mismo token no sirve, así que se cierra la sesión.
// /login y /validateSession manejan su propio 401 (credenciales incorrectas y
// chequeo inicial en AllRoutes).
const handleResponseError = (error: any): Promise<AxiosError> => {
  const url: string = error.config?.url || '';
  const isAuthRequest = url.includes('/validateSession') || url.includes('/login');

  if (error.response?.status === 401 && !isAuthRequest) {
    notifySessionExpired();
  }
  if (error.response?.status === 403 && error.response?.data?.code === 'PASSWORD_CHANGE_REQUIRED') {
    notifyPasswordChangeRequired();
  }
  console.error('Axios interceptor error', error);
  return Promise.reject(error);
};

// Las instancias de axios.create() no heredan los interceptors del singleton,
// por eso se registra el mismo handler en cada una.
const createClient = (accessToken: string): AxiosInstance => {
  const client = axios.create({
    baseURL: useBackendProxy ? BACKEND_PROXY_PATH : HOST_GROWME,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': accessToken ? `Bearer ${accessToken}` : '',
      // Solo por el proxy (mismo dominio, sin preflight CORS): el back lo anota en el log.
      ...(useBackendProxy ? { 'X-GrowMe-Via': 'proxy' } : {}),
    },
  });
  client.interceptors.response.use((response) => response, handleResponseError);
  return client;
}

const initializeClient = async (): Promise<AxiosInstance> => {
  try {
    const { accessToken } = await getAccessToken();

    return createClient(accessToken);
  } catch (error) {
    console.error('Error initializing Axios client:', error);
    //@ts-ignore
    if (error.accessToken === "") {
      return createClient('');
    }
    throw new AxiosError('Error initializing Axios client');
  }
}

axios.interceptors.response.use(
  (response: AxiosResponse): AxiosResponse => response,
  handleResponseError
);

const methods = {
  get: async (url: string, params?: any) => (await initializeClient()).get(url, { params }),
  post: async (url: string, body?: any, config?: any) => {
    const { params, headers, timeout } = config || {};
    return (await initializeClient()).post(url, body, { params, headers, timeout });
  },
  put: async (url: string, body?: any, params?: any) => (await initializeClient()).put(url, body, { params }),
  del: async (url: string, body?: any, params?: any) => (await initializeClient()).delete(url, { data: body, params }),
  download: async (url: string, params?: any) => (await initializeClient()).get(url, { 
    params, 
    responseType: 'arraybuffer' 
  }),
};

export { methods };
